// src/store/builders/useJobStore.js
import { create } from "zustand";
import { toast } from "react-hot-toast";
import { axiosEnquiryInstance as axios } from "../../lib/axiosInstance";
import useAuthStore from "../useAuthStore";

const useJobStore = create((set, get) => ({
    jobs: [],
    isLoading: false,      // For fetching the list
    isSubmitting: false,   // For create/update/delete operations
    error: null,

    // Fetch all jobs for the logged-in builder
    fetchJobs: async () => {
        set({ isLoading: true, error: null });
        const { token, user } = useAuthStore.getState();
        if (!token || !user?.id) {
            set({ isLoading: false, error: "Not authenticated" });
            return;
        }

        try {
            const response = await axios.get('/jobs');
            if (response.data.success) {
                // The public GET endpoint returns all jobs, so we filter for the current builder
                // CORRECTED: Compare job's numeric builder_profile_id with the user's numeric id
                const myJobs = (response.data.jobs || []).filter(job => job.builder_profile_id === user.id);
                set({ jobs: myJobs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)), isLoading: false });
            } else {
                throw new Error(response.data.message || "Failed to fetch jobs");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Error fetching jobs.";
            toast.error(errorMsg);
            set({ isLoading: false, error: errorMsg, jobs: [] });
        }
    },

    // Create a new job
    createJob: async (jobData) => {
        set({ isSubmitting: true, error: null });
        const { token, user } = useAuthStore.getState(); // Get user object
        if (!token || !user?.builderUniqueId) { // Check for user and builderUniqueId
            toast.error("Authentication required.");
            set({ isSubmitting: false });
            return { success: false };
        }

        // --- MODIFICATION: Add builder ducktail ID to the payload ---
        const payload = {
            ...jobData,
            builder_ducktail_id: user.builderUniqueId
        };

        try {
            const response = await axios.post('/jobs', payload, { // Send the modified payload
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                toast.success(response.data.message);
                set(state => ({ jobs: [response.data.job, ...state.jobs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) }));
                return { success: true };
            } else {
                throw new Error(response.data.message || "Failed to create job.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Error creating job.";
            toast.error(errorMsg);
            set({ error: errorMsg });
            return { success: false };
        } finally {
            set({ isSubmitting: false });
        }
    },

    // Update a job
    updateJob: async (jobId, jobData) => {
        set({ isSubmitting: true, error: null });
        const { token, user } = useAuthStore.getState();
        if (!token || !user?.builderUniqueId) {
            toast.error("Authentication required.");
            set({ isSubmitting: false });
            return { success: false };
        }

        const payload = {
            ...jobData,
            builder_ducktail_id: user.builderUniqueId,
        };

        try {
            const response = await axios.put(`/jobs/${jobId}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                toast.success(response.data.message);
                set(state => ({
                    jobs: state.jobs.map(job =>
                        job.id === jobId ? response.data.job : job
                    ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                }));
                return { success: true };
            } else {
                throw new Error(response.data.message || "Failed to update job.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Error updating job.";
            toast.error(errorMsg);
            set({ error: errorMsg });
            return { success: false };
        } finally {
            set({ isSubmitting: false });
        }
    },

    // Update Job Status
    updateJobStatus: async (jobId, status) => {
        set({ isSubmitting: true, error: null });
        const { token, user } = useAuthStore.getState();
        if (!token || !user?.builderUniqueId) {
            toast.error("Authentication required.");
            set({ isSubmitting: false });
            return { success: false };
        }

        const payload = {
            builder_ducktail_id: user.builderUniqueId,
            status: status
        };

        try {
            const response = await axios.put(`/jobs/${jobId}/status`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                toast.success(response.data.message);
                set(state => ({
                    jobs: state.jobs.map(job =>
                        job.id === jobId ? { ...job, status: response.data.job.status } : job
                    )
                }));
                return { success: true };
            } else {
                throw new Error(response.data.message || "Failed to update job status.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Error updating job status.";
            toast.error(errorMsg);
            set({ error: errorMsg });
            return { success: false };
        } finally {
            set({ isSubmitting: false });
        }
    },

    // Delete a job
    deleteJob: async (jobId) => {
        set({ isSubmitting: true, error: null });
        const { token, user } = useAuthStore.getState();
        if (!token || !user?.builderUniqueId) {
            toast.error("Authentication required.");
            set({ isSubmitting: false });
            return { success: false };
        }

        const payload = {
            builder_ducktail_id: user.builderUniqueId
        };

        try {
            const response = await axios.delete(`/jobs/${jobId}`, {
                headers: { Authorization: `Bearer ${token}` },
                data: payload
            });

            if (response.data.success) {
                toast.success(response.data.message);
                set(state => ({
                    jobs: state.jobs.filter(job => job.id !== jobId)
                }));
                return { success: true };
            } else {
                throw new Error(response.data.message || "Failed to delete job.");
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || "Error deleting job.";
            toast.error(errorMsg);
            set({ error: errorMsg });
            return { success: false };
        } finally {
            set({ isSubmitting: false });
        }
    },
}));

export default useJobStore;